import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  Plus, Trash2, RefreshCw, Pencil, AlertCircle, CheckCircle2,
  Sparkles, School, Unlink,
} from "lucide-react";
import { toast } from "sonner";
import { IcalShareCard } from "@/components/IcalShareCard";
import { GoogleCalendarPicker } from "@/components/settings/GoogleCalendarPicker";

export function CalendarsSection({ ctx }) {
  const {
    feeds, students, schedulerStatus, currentUser,
    briefingBusy, setBriefingBusy, briefingPreview, setBriefingPreview,
    openCreate, openEdit, refresh, remove,
    openStudentDialog, removeStudentCredentials,
  } = ctx;

  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-base font-medium">iCal-kalendrar</h3>
        <div className="flex gap-1.5">
          <Button
            onClick={openCreate}
            size="sm"
            className="rounded-full bg-[color:var(--moss)] hover:bg-[#1f421f] h-8"
            data-testid="add-feed-button"
          >
            <Plus size={14} className="mr-1" /> Lägg till
          </Button>
        </div>
      </div>
      {schedulerStatus && (
        <p
          className="text-[10px] text-[color:var(--text-tertiary)] mb-3 flex items-center gap-1.5"
          data-testid="scheduler-status"
        >
          <RefreshCw size={10} />
          Auto-uppdateras var {schedulerStatus.interval_hours}:e timme
          {schedulerStatus.last_run && (
            <span>· senast {new Date(schedulerStatus.last_run).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
              {schedulerStatus.events_count != null && ` (${schedulerStatus.events_count} händelser)`}
            </span>
          )}
          {schedulerStatus.next_run && (
            <span>· nästa {new Date(schedulerStatus.next_run).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</span>
          )}
        </p>
      )}

      <IcalShareCard />

      {schedulerStatus && (
        <div
          className="card-soft p-3 mb-4 flex items-start gap-2.5"
          data-testid="briefing-card"
        >
          <Sparkles size={14} className="text-[color:var(--jarvis)] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium">Söndagsbriefing</p>
            <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
              Edit skickar en peppande veckosammanfattning som push söndag 18:00.
              {schedulerStatus.briefing_next_run && (
                <span> · nästa {new Date(schedulerStatus.briefing_next_run).toLocaleString("sv-SE", { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span>
              )}
              {schedulerStatus.briefing_last_run && (
                <span> · senast {new Date(schedulerStatus.briefing_last_run).toLocaleDateString("sv-SE")}</span>
              )}
            </p>
            {briefingPreview && (
              <p className="text-xs text-[color:var(--text-secondary)] mt-2 whitespace-pre-wrap p-2 bg-[color:var(--bg-elevated)] rounded-lg">
                {briefingPreview}
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              setBriefingBusy(true);
              try {
                const { data } = await api.post("/jarvis/briefing/send-now");
                setBriefingPreview(data.text);
                toast.success(data.push_sent ? `Briefing skickad (${data.push_sent} notis)` : "Briefing skapad (ingen push registrerad)");
              } catch (e) {
                toast.error("Kunde inte skapa briefing");
              } finally {
                setBriefingBusy(false);
              }
            }}
            disabled={briefingBusy}
            className="rounded-full h-7 text-[11px]"
            data-testid="briefing-send-now"
          >
            {briefingBusy ? "Skickar…" : "Testa nu"}
          </Button>
          {currentUser?.role === "admin" && (
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm("Skicka söndagsbriefingen till HELA familjen nu?")) return;
                setBriefingBusy(true);
                try {
                  const { data } = await api.post("/jarvis/briefing/send-all-now");
                  toast.success(`Köat i bakgrunden (job ${data.job_id.slice(0, 6)}) — följ progress på Hälsa-sidan`);
                } catch (e) {
                  toast.error("Kunde inte köa: " + (e?.response?.data?.detail || e.message));
                } finally {
                  setBriefingBusy(false);
                }
              }}
              disabled={briefingBusy}
              className="rounded-full h-7 text-[11px] ml-2 bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="briefing-send-all-now"
            >
              {briefingBusy ? "Köar…" : "Skicka till alla"}
            </Button>
          )}
        </div>
      )}

      {feeds.length === 0 ? (
        <div className="card-soft p-6 text-center" data-testid="feeds-empty">
          <p className="text-sm text-[color:var(--text-secondary)]">
            Lägg till barnens skolkalender (iCal/webcal-URL) för att se händelser och läxor.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 stagger">
          {feeds.map((f) => (
            <li key={f.id} className="card-soft p-3.5 fade-up" data-testid={`feed-${f.id}`}>
              <div className="flex items-start gap-3">
                <div
                  className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: f.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{f.name}</p>
                    {f.owner && (
                      <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
                        · {f.owner}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[color:var(--text-tertiary)] truncate mt-0.5">{f.url}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {f.last_error ? (
                      <span className="text-[10px] flex items-center gap-1 text-[#DC2626]">
                        <AlertCircle size={11} /> {f.last_error.slice(0, 50)}
                      </span>
                    ) : f.last_synced ? (
                      <span className="text-[10px] flex items-center gap-1 text-[color:var(--moss)]">
                        <CheckCircle2 size={11} /> {f.event_count} händelser
                      </span>
                    ) : (
                      <span className="text-[10px] text-[color:var(--text-tertiary)]">Inte synkad ännu</span>
                    )}
                    {f.source === "vklass" && (() => {
                      const st = students.find((s) => s.feed_id === f.id);
                      return st ? (
                        <span className="text-[10px] flex items-center gap-1 text-[color:var(--moss)] font-medium">
                          <School size={11} /> Elev-inlogg ✓
                          {st.assignment_count != null && ` (${st.assignment_count} uppg.)`}
                        </span>
                      ) : (
                        <button
                          onClick={() => openStudentDialog(f)}
                          className="text-[10px] underline text-[color:var(--moss)]"
                          data-testid={`add-student-${f.id}`}
                        >
                          + Lägg till elev-inlogg
                        </button>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => refresh(f.id)}
                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)]"
                    data-testid={`feed-refresh-${f.id}`}
                  >
                    <RefreshCw size={14} />
                  </button>
                  <button
                    onClick={() => openEdit(f)}
                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)]"
                    data-testid={`feed-edit-${f.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  {f.source === "vklass" && students.find((s) => s.feed_id === f.id) && (
                    <button
                      onClick={() => removeStudentCredentials(f.id)}
                      className="p-1.5 rounded text-[color:var(--text-secondary)] hover:text-[#DC2626]"
                      title="Ta bort elev-inlogg"
                      data-testid={`remove-student-${f.id}`}
                    >
                      <Unlink size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => remove(f.id)}
                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)] hover:text-[#DC2626]"
                    data-testid={`feed-delete-${f.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Google Calendar-synk (separat block under iCal-feeds) */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <GoogleCalendarPicker />
      </div>
    </>
  );
}
